import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

// The site's loading mark (Felipe, Oct 17: "make spinners fun, beach theme"):
// a sun coming up over two rolling waves with a board bobbing on them. CSS
// only, under 2 KB, the same in every market (no city names). Reduced
// motion gets a gentle fade instead of movement. Page-level waits use `lg`
// with a line of text; inline waits use `sm`.

const LINES = [
  "Waxing the board",
  "Checking the tide",
  "Shaking out the sand",
  "Waiting for the set",
  "Rinsing the wetsuit",
  "Reading the swell",
];

interface Props {
  size?: "sm" | "lg";
  /** Show a rotating line under the scene (default: only for `lg`). */
  label?: boolean;
  className?: string;
}

export function BeachLoader({ size = "lg", label = size === "lg", className }: Props) {
  const [i, setI] = useState(() => Math.floor(Math.random() * LINES.length));
  useEffect(() => {
    if (!label) return;
    const t = window.setInterval(() => setI((n) => (n + 1) % LINES.length), 2000);
    return () => window.clearInterval(t);
  }, [label]);
  const px = size === "lg" ? 96 : 28;
  return (
    <div role="status" aria-label="Loading" className={cn("flex flex-col items-center justify-center gap-3 text-muted-foreground", className)}>
      <svg
        width={px}
        height={Math.round(px * 0.625)}
        viewBox="0 0 96 60"
        aria-hidden
        className="motion-reduce:animate-pulse overflow-visible"
      >
        <defs>
          <clipPath id="beach-sea">
            <rect x="0" y="0" width="96" height="60" />
          </clipPath>
        </defs>
        {/* sun */}
        <circle cx="66" cy="30" r="9" fill="hsl(var(--primary))" className="animate-beach-sun motion-reduce:animate-none" />
        <g clipPath="url(#beach-sea)">
          {/* back wave */}
          <path
            d="M-48 40 Q-36 32 -24 40 T0 40 T24 40 T48 40 T72 40 T96 40 T120 40 T144 40 V60 H-48 Z"
            fill="hsl(var(--primary) / 0.25)"
            className="animate-beach-wave-slow motion-reduce:animate-none"
          />
          {/* board */}
          <g className="animate-beach-bob motion-reduce:animate-none" style={{ transformOrigin: "40px 40px" }}>
            <ellipse cx="40" cy="39" rx="14" ry="3.2" fill="hsl(var(--foreground))" />
            <line x1="32" y1="39" x2="48" y2="39" stroke="hsl(var(--background))" strokeWidth="1" />
          </g>
          {/* front wave */}
          <path
            d="M-48 46 Q-36 38 -24 46 T0 46 T24 46 T48 46 T72 46 T96 46 T120 46 T144 46 V60 H-48 Z"
            fill="hsl(var(--primary) / 0.55)"
            className="animate-beach-wave motion-reduce:animate-none"
          />
        </g>
      </svg>
      {label ? <p className="text-sm" aria-live="polite">{LINES[i]}…</p> : null}
    </div>
  );
}

export default BeachLoader;
